<template>
  <div class="p-4 md:p-6">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
          <Icon icon="mdi:clipboard-clock" class="text-blue-500" width="28" height="28" />
          Control de Asistencia
        </h1>
        <p class="text-sm md:text-base text-gray-600 dark:text-gray-400">
          Monitoreo de asistencia de empleados con huella digital
        </p>
      </div>
      <div class="flex items-center gap-3 flex-wrap">
        <div class="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-700">
          <span class="w-2.5 h-2.5 rounded-full" :class="estadoLector ? 'bg-green-500 animate-pulse' : 'bg-red-500'"></span>
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ estadoLector ? 'Lector conectado' : 'Lector desconectado' }}
          </span>
        </div>
        <button
          @click="toggleConexion"
          :disabled="conectando"
          class="px-4 py-2.5 rounded-lg text-white transition-colors text-sm"
          :class="estadoLector ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'"
        >
          <Icon :icon="estadoLector ? 'mdi:power-off' : 'mdi:power-on'" class="mr-2" />
          {{ conectando ? 'Conectando...' : estadoLector ? 'Desconectar' : 'Conectar' }}
        </button>
        <button
          @click="registrarManual"
          class="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm flex items-center"
        >
          <Icon icon="mdi:hand-back-right" class="mr-2" />
          Registrar Manual
        </button>
        <button
          @click="recargarDatos"
          :disabled="cargando"
          class="px-4 py-2.5 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-white rounded-lg transition-colors text-sm flex items-center"
        >
          <Icon icon="mdi:refresh" class="mr-2" :class="{ 'animate-spin': cargando }" />
          Actualizar
        </button>
      </div>
    </div>

    <!-- Tarjetas de estadísticas -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Registros hoy</p>
            <p class="text-2xl font-bold text-gray-800 dark:text-white">{{ estadisticas.totalRegistrosHoy || 0 }}</p>
          </div>
          <div class="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
            <Icon icon="mdi:calendar-today" class="text-blue-600 dark:text-blue-400 text-xl" />
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Entradas</p>
            <p class="text-2xl font-bold text-green-600 dark:text-green-400">{{ estadisticas.totalEntradasHoy || 0 }}</p>
          </div>
          <div class="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
            <Icon icon="mdi:login" class="text-green-600 dark:text-green-400 text-xl" />
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Salidas</p>
            <p class="text-2xl font-bold text-red-600 dark:text-red-400">{{ estadisticas.totalSalidasHoy || 0 }}</p>
          </div>
          <div class="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
            <Icon icon="mdi:logout" class="text-red-600 dark:text-red-400 text-xl" />
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Usuarios con huella</p>
            <p class="text-2xl font-bold text-purple-600 dark:text-purple-400">{{ estadisticas.usuariosActivosConHuella || 0 }}</p>
          </div>
          <div class="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
            <Icon icon="mdi:fingerprint" class="text-purple-600 dark:text-purple-400 text-xl" />
          </div>
        </div>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-200 dark:border-gray-700 mb-6">
      <div class="flex flex-col md:flex-row gap-4">
        <div class="flex-1">
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              v-model="searchQuery"
              @input="handleSearch"
              type="text"
              placeholder="Buscar por nombre, usuario o rol..."
              class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
        </div>
        <div class="flex flex-wrap gap-3">
          <select
            v-model="filterTipo"
            class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="all">Todos los tipos</option>
            <option value="entrada">Entrada</option>
            <option value="salida">Salida</option>
          </select>
          
          <select
            v-model="filterMetodo"
            class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="all">Todos los métodos</option>
            <option value="huella">Huella</option>
            <option value="manual">Manual</option>
          </select>
          
          <select
            v-model="sortBy"
            class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="fecha_desc">Más reciente</option>
            <option value="fecha_asc">Más antiguo</option>
            <option value="nombre">Nombre A-Z</option>
            <option value="nombre_desc">Nombre Z-A</option>
          </select>
          
          <button
            @click="clearFilters"
            class="px-3 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors text-sm"
          >
            <Icon icon="mdi:close-circle" class="mr-1" />
            Limpiar
          </button>
        </div>
      </div>
    </div>

    <!-- Tabla de registros -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Usuario</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tipo</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Hora</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Método</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rol</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="cargando" class="text-center">
              <td colspan="6" class="px-4 py-12">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p class="text-gray-500 mt-2 text-sm">Cargando registros...</p>
              </td>
            </tr>
            <tr v-else-if="filteredRegistros.length === 0" class="text-center">
              <td colspan="6" class="px-4 py-12 text-gray-400">
                <Icon icon="mdi:clipboard-text-off" class="text-5xl mx-auto" />
                <p class="mt-2">No hay registros de asistencia para hoy</p>
                <p class="text-sm">Los registros aparecerán aquí cuando los empleados marquen su asistencia</p>
              </td>
            </tr>
            <tr v-for="registro in paginatedRegistros" :key="registro.idRegistro" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <td class="px-4 py-3">
                <div class="flex items-center gap-3">
                  <img
                    :src="getImageUrl(registro.fotoPerfilUrl) || defaultAvatar"
                    class="w-8 h-8 rounded-full object-cover"
                    @error="handleImageError"
                  />
                  <div>
                    <p class="font-medium text-gray-800 dark:text-white text-sm">{{ registro.nombreCompleto || 'Usuario' }}</p>
                    <p class="text-xs text-gray-500">@{{ registro.nombreUsuario || 'sin usuario' }}</p>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="registro.tipoRegistro === 'entrada' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'"
                >
                  <Icon :icon="registro.tipoRegistro === 'entrada' ? 'mdi:login' : 'mdi:logout'" class="mr-1 text-xs" />
                  {{ registro.tipoRegistro === 'entrada' ? 'Entrada' : 'Salida' }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                {{ formatTime(registro.fechaHora) }}
              </td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="registro.metodoVerificacion === 'huella' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'"
                >
                  <Icon :icon="registro.metodoVerificacion === 'huella' ? 'mdi:fingerprint' : 'mdi:hand-back-right'" class="mr-1 text-xs" />
                  {{ registro.metodoVerificacion === 'huella' ? 'Huella' : 'Manual' }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                {{ registro.rol || 'Sin rol' }}
              </td>
              <td class="px-4 py-3 text-right">
                <button
                  @click="verDetalle(registro)"
                  class="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                >
                  <Icon icon="mdi:eye" class="text-lg" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="filteredRegistros.length > 0" class="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-3">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {{ startItem }} - {{ endItem }} de {{ filteredRegistros.length }} registros
        </p>
        <div class="flex gap-1">
          <button
            @click="currentPage = Math.max(1, currentPage - 1)"
            :disabled="currentPage === 1"
            class="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Icon icon="mdi:chevron-left" />
          </button>
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="currentPage = page"
            class="px-3 py-1 rounded-lg text-sm transition-colors"
            :class="currentPage === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'"
          >
            {{ page }}
          </button>
          <button
            @click="currentPage = Math.min(totalPages, currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Icon icon="mdi:chevron-right" />
          </button>
        </div>
      </div>
    </div>

    <!-- ========== MODAL REGISTRO MANUAL ========== -->
    <Teleport to="body">
      <div
        v-if="showManualModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4"
        @click.self="closeManualModal"
      >
        <div class="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-xl font-bold text-gray-800 dark:text-white flex items-center">
              <Icon icon="mdi:hand-back-right" class="mr-2 text-blue-500" />
              Registrar Manualmente
            </h3>
            <button
              @click="closeManualModal"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <Icon icon="mdi:close" class="text-2xl" />
            </button>
          </div>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Usuario <span class="text-red-500">*</span>
              </label>
              <select
                v-model="manualForm.idUsuario"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Seleccionar usuario...</option>
                <option v-for="usuario in usuariosConHuella" :key="usuario.idUsuario" :value="usuario.idUsuario">
                  {{ usuario.nombreCompleto }} (@{{ usuario.nombreUsuario }})
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo de registro <span class="text-red-500">*</span>
              </label>
              <div class="flex gap-3">
                <button
                  @click="manualForm.tipoRegistro = 'entrada'"
                  class="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  :class="manualForm.tipoRegistro === 'entrada' ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'"
                >
                  <Icon icon="mdi:login" class="mr-1" />
                  Entrada
                </button>
                <button
                  @click="manualForm.tipoRegistro = 'salida'"
                  class="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  :class="manualForm.tipoRegistro === 'salida' ? 'bg-red-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'"
                >
                  <Icon icon="mdi:logout" class="mr-1" />
                  Salida
                </button>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Observaciones
              </label>
              <textarea
                v-model="manualForm.observaciones"
                rows="2"
                placeholder="Opcional: motivo del registro manual"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm resize-none"
              ></textarea>
            </div>

            <div class="flex gap-3 pt-2">
              <button
                @click="closeManualModal"
                class="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                @click="guardarRegistroManual"
                :disabled="guardandoManual || !manualForm.idUsuario"
                class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <Icon v-if="guardandoManual" icon="mdi:loading" class="animate-spin mr-2" />
                {{ guardandoManual ? 'Guardando...' : 'Registrar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ========== MODAL DETALLE ========== -->
    <Teleport to="body">
      <div
        v-if="showDetalleModal && detalleRegistro"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4"
        @click.self="closeDetalleModal"
      >
        <div class="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-xl font-bold text-gray-800 dark:text-white flex items-center">
              <Icon icon="mdi:clipboard-text" class="mr-2 text-blue-500" />
              Detalle del Registro
            </h3>
            <button
              @click="closeDetalleModal"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <Icon icon="mdi:close" class="text-2xl" />
            </button>
          </div>

          <div class="space-y-4">
            <div class="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <img
                :src="getImageUrl(detalleRegistro.fotoPerfilUrl) || defaultAvatar"
                class="w-16 h-16 rounded-full object-cover"
                @error="handleImageError"
              />
              <div>
                <p class="font-semibold text-gray-800 dark:text-white text-lg">{{ detalleRegistro.nombreCompleto || 'Usuario' }}</p>
                <p class="text-sm text-gray-500">@{{ detalleRegistro.nombreUsuario || 'sin usuario' }}</p>
                <p class="text-sm text-gray-500">{{ detalleRegistro.rol || 'Sin rol' }}</p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p class="text-xs text-gray-500">Tipo</p>
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium"
                  :class="detalleRegistro.tipoRegistro === 'entrada' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                >
                  <Icon :icon="detalleRegistro.tipoRegistro === 'entrada' ? 'mdi:login' : 'mdi:logout'" class="mr-1" />
                  {{ detalleRegistro.tipoRegistro === 'entrada' ? 'Entrada' : 'Salida' }}
                </span>
              </div>
              <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p class="text-xs text-gray-500">Método</p>
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium"
                  :class="detalleRegistro.metodoVerificacion === 'huella' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'"
                >
                  <Icon :icon="detalleRegistro.metodoVerificacion === 'huella' ? 'mdi:fingerprint' : 'mdi:hand-back-right'" class="mr-1" />
                  {{ detalleRegistro.metodoVerificacion === 'huella' ? 'Huella' : 'Manual' }}
                </span>
              </div>
              <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p class="text-xs text-gray-500">Fecha</p>
                <p class="text-sm font-medium">{{ formatDate(detalleRegistro.fechaHora) }}</p>
              </div>
              <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p class="text-xs text-gray-500">Hora</p>
                <p class="text-sm font-medium">{{ formatTime(detalleRegistro.fechaHora) }}</p>
              </div>
            </div>

            <button
              @click="closeDetalleModal"
              class="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ========== TOAST ========== -->
    <div
      v-if="toast.show"
      class="fixed bottom-4 right-4 max-w-sm w-full rounded-lg shadow-lg p-4 transition-all duration-300 z-[100]"
      :class="toast.type === 'success' ? 'bg-green-500' : toast.type === 'error' ? 'bg-red-500' : toast.type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'"
    >
      <div class="flex items-start gap-3">
        <Icon :icon="toast.type === 'success' ? 'mdi:check-circle' : toast.type === 'error' ? 'mdi:alert-circle' : toast.type === 'warning' ? 'mdi:alert' : 'mdi:information'" class="text-white text-xl flex-shrink-0" />
        <div>
          <p class="text-white font-medium">{{ toast.title }}</p>
          <p class="text-white/80 text-sm">{{ toast.message }}</p>
        </div>
        <button @click="toast.show = false" class="text-white/60 hover:text-white ml-auto">
          <Icon icon="mdi:close" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import * as signalR from '@microsoft/signalr';
import {
  obtenerAsistenciaHoy,
  obtenerEstadisticasAsistencia,
  obtenerEstadoLector,
  conectarLector,
  desconectarLector,
  obtenerUsuariosConHuella,
  registrarAsistenciaManual
} from '@/api/huella.js';
import { obtenerUrlImagen } from '@/api/usuarios.js';

const API_BASE_URL = (typeof process !== 'undefined' && process.env && process.env.VUE_APP_API_BASE_URL)
  || 'http://localhost:5090';
const HUB_URL = `${API_BASE_URL}/asistenciaHub`;

export default {
  name: 'AsistenciaList',
  components: { Icon },
  data() {
    return {
      // Datos principales
      registros: [],
      usuariosConHuella: [],
      
      // UI States
      cargando: false,
      conectando: false,
      guardandoManual: false,
      estadoLector: false,
      
      // Filtros
      searchQuery: '',
      filterTipo: 'all',
      filterMetodo: 'all',
      fechaFiltro: dayjs().format('YYYY-MM-DD'),
      sortBy: 'fecha_desc',
      currentPage: 1,
      itemsPerPage: 8,
      
      // Estadísticas
      estadisticas: {
        totalUsuariosConHuella: 0,
        totalRegistrosHoy: 0,
        totalEntradasHoy: 0,
        totalSalidasHoy: 0,
        usuariosActivosConHuella: 0
      },
      
      // Último registro
      ultimoRegistro: null,
      lastUpdate: new Date(),
      defaultAvatar: 'https://ui-avatars.com/api/?name=Usuario&background=4F46E5&color=fff&size=100',
      
      // 🔥 CONEXIÓN SIGNALR
      hubConnection: null,
      
      // Toast
      toast: {
        show: false,
        type: 'success',
        title: '',
        message: ''
      },
      
      // Modal registro manual
      showManualModal: false,
      manualForm: {
        idUsuario: '',
        tipoRegistro: 'entrada',
        observaciones: ''
      },
      
      // Modal detalle
      showDetalleModal: false,
      detalleRegistro: null,
    };
  },
  computed: {
    filteredRegistros() {
      let filtered = [...this.registros];
      
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(r => 
          (r.nombreCompleto && r.nombreCompleto.toLowerCase().includes(query)) ||
          (r.nombreUsuario && r.nombreUsuario.toLowerCase().includes(query)) ||
          (r.rol && r.rol.toLowerCase().includes(query))
        );
      }
      
      if (this.filterTipo !== 'all') {
        filtered = filtered.filter(r => r.tipoRegistro === this.filterTipo);
      }
      
      if (this.filterMetodo !== 'all') {
        filtered = filtered.filter(r => r.metodoVerificacion === this.filterMetodo);
      }
      
      filtered = this.ordenarRegistros(filtered, this.sortBy);
      
      return filtered;
    },
    
    paginatedRegistros() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredRegistros.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredRegistros.length / this.itemsPerPage);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    
    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredRegistros.length);
    }
  },
  watch: {
    filterTipo() { this.currentPage = 1; },
    filterMetodo() { this.currentPage = 1; },
    searchQuery() { this.currentPage = 1; },
    sortBy() { this.currentPage = 1; },
  },
  mounted() {
    this.init();
    this.conectarSignalR();
  },
  beforeUnmount() {
    if (this.hubConnection) {
      this.hubConnection.stop();
    }
  },
  methods: {
    // ========== INICIALIZACIÓN ==========
    async init() {
      await this.cargarAsistencia();
      await this.cargarEstadisticas();
      await this.cargarEstadoLector();
      await this.cargarUsuariosConHuella();
    },
    
    // ========== SIGNALR ==========
    async conectarSignalR() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      console.log('🔗 Conectando a SignalR en:', HUB_URL);
      
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(HUB_URL, {
          accessTokenFactory: () => token || '',
          transport: signalR.HttpTransportType.WebSocket | signalR.HttpTransportType.LongPolling
        })
        .withAutomaticReconnect()
        .build();

      // 🔥 Evento cuando se detecta un dedo
      this.hubConnection.on('HuellaProcesando', (data) => {
        console.log('📢 HuellaProcesando recibido:', data);
        this.mostrarToast('info', '🖐️ Dedo detectado', 'Procesando huella...');
      });

      // 🔥 EVENTO PRINCIPAL: Cuando se registra una asistencia
      this.hubConnection.on('HuellaDetectada', (data) => {
        console.log('📢 HuellaDetectada recibido en AsistenciaList:', data);
        
        if (data?.exito) {
          // ✅ Mostrar notificación de éxito
          const tipo = data.tipoRegistro === 'entrada' ? '✅ ENTRADA' : '🚪 SALIDA';
          const mensaje = `${tipo} registrada para ${data.nombreCompleto} a las ${this.formatTime(data.fechaHora)}`;
          this.mostrarToast('success', 'Registro exitoso', mensaje);
          
          // ✅ Recargar los datos automáticamente
          this.cargarAsistencia();
          this.cargarEstadisticas();
        } else {
          // ❌ Mostrar error
          this.mostrarToast('error', 'Error', data?.mensaje || 'Huella no reconocida');
        }
      });

      // Evento de error
      this.hubConnection.on('HuellaError', (data) => {
        console.log('📢 HuellaError recibido:', data);
        this.mostrarToast('error', 'Error del lector', data?.mensaje || 'Error al leer la huella');
      });

      // Evento de cambio de estado del lector
      this.hubConnection.on('EstadoLector', (data) => {
        console.log('📢 EstadoLector recibido:', data);
        this.estadoLector = !!data?.conectado;
      });

      // Evento para actualizar asistencia
      this.hubConnection.on('ActualizarAsistencia', () => {
        console.log('📢 ActualizarAsistencia recibido');
        this.cargarAsistencia();
        this.cargarEstadisticas();
      });

      try {
        await this.hubConnection.start();
        console.log('✅ SignalR conectado exitosamente en AsistenciaList');
      } catch (error) {
        console.error('❌ Error conectando SignalR:', error);
        // Intentar sin token si falla
        try {
          this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
              transport: signalR.HttpTransportType.WebSocket | signalR.HttpTransportType.LongPolling
            })
            .withAutomaticReconnect()
            .build();
          await this.hubConnection.start();
          console.log('✅ SignalR conectado sin token');
        } catch (retryError) {
          console.error('❌ Error en reintento de SignalR:', retryError);
        }
      }
    },
    
    // ========== CARGA DE DATOS ==========
    async cargarAsistencia() {
      this.cargando = true;
      try {
        const response = await obtenerAsistenciaHoy();
        console.log('📊 cargarAsistencia - response:', response);
        
        if (response.success && response.data) {
          this.registros = response.data.registros || [];
          this.estadisticas.totalRegistrosHoy = response.data.totalRegistros || 0;
          this.estadisticas.totalEntradasHoy = response.data.entradas || 0;
          this.estadisticas.totalSalidasHoy = response.data.salidas || 0;
          
          if (this.registros.length > 0) {
            this.ultimoRegistro = this.registros[0];
          }
        } else {
          this.registros = [];
          this.estadisticas.totalRegistrosHoy = 0;
          this.estadisticas.totalEntradasHoy = 0;
          this.estadisticas.totalSalidasHoy = 0;
        }
        this.lastUpdate = new Date();
      } catch (error) {
        console.error('Error cargando asistencia:', error);
        this.registros = [];
        this.estadisticas.totalRegistrosHoy = 0;
        this.estadisticas.totalEntradasHoy = 0;
        this.estadisticas.totalSalidasHoy = 0;
        this.mostrarToast('error', 'Error', 'No se pudo cargar la asistencia');
      } finally {
        this.cargando = false;
      }
    },
    
    async cargarEstadisticas() {
      try {
        const response = await obtenerEstadisticasAsistencia();
        console.log('📊 cargarEstadisticas - response:', response);
        
        if (response.success && response.data) {
          this.estadisticas.totalUsuariosConHuella = response.data.totalUsuariosConHuella || 0;
          this.estadisticas.usuariosActivosConHuella = response.data.usuariosActivosConHuella || 0;
        }
      } catch (error) {
        console.error('Error cargando estadísticas:', error);
      }
    },
    
    async cargarEstadoLector() {
      try {
        const response = await obtenerEstadoLector();
        if (response.success && response.data) {
          this.estadoLector = response.data.conectado || false;
        }
      } catch (error) {
        console.error('Error cargando estado:', error);
        this.estadoLector = false;
      }
    },
    
    async cargarUsuariosConHuella() {
      try {
        const response = await obtenerUsuariosConHuella();
        if (response.success && response.data) {
          this.usuariosConHuella = response.data.map(user => ({
            ...user,
            nombreCompleto: user.nombreCompleto || `${user.nombre || ''} ${user.apellido || ''}`.trim() || 'Usuario',
            rol: user.rol || 'Sin rol',
            nombreUsuario: user.nombreUsuario || user.email || 'sin usuario'
          }));
        }
      } catch (error) {
        console.error('Error cargando usuarios con huella:', error);
      }
    },
    
    // ========== CONEXIÓN DEL LECTOR ==========
    async toggleConexion() {
      this.conectando = true;
      try {
        if (this.estadoLector) {
          const response = await desconectarLector();
          if (response.success) {
            this.estadoLector = false;
            this.mostrarToast('success', 'Desconectado', 'Lector desconectado correctamente');
          } else {
            this.mostrarToast('error', 'Error', response.error || 'Error al desconectar');
          }
        } else {
          const response = await conectarLector();
          if (response.success && response.data?.exito) {
            this.estadoLector = true;
            this.mostrarToast('success', 'Conectado', 'Lector conectado correctamente');
          } else {
            this.mostrarToast('error', 'Error', response.data?.mensaje || response.error || 'No se pudo conectar');
          }
        }
      } catch (error) {
        console.error('Error en conexión:', error);
        this.mostrarToast('error', 'Error', error.message || 'Error al conectar');
      } finally {
        this.conectando = false;
      }
    },
    
    // ========== REGISTRO MANUAL ==========
    registrarManual() {
      if (this.usuariosConHuella.length === 0) {
        this.mostrarToast('warning', 'Sin usuarios', 'No hay usuarios con huella registrada');
        return;
      }
      this.manualForm.idUsuario = '';
      this.manualForm.tipoRegistro = 'entrada';
      this.manualForm.observaciones = '';
      this.showManualModal = true;
    },
    
    closeManualModal() {
      this.showManualModal = false;
    },
    
    async guardarRegistroManual() {
      if (!this.manualForm.idUsuario) {
        this.mostrarToast('error', 'Error', 'Selecciona un usuario');
        return;
      }
      
      this.guardandoManual = true;
      try {
        const response = await registrarAsistenciaManual(
          this.manualForm.idUsuario,
          this.manualForm.tipoRegistro
        );
        
        if (response.success && response.data?.exito) {
          this.closeManualModal();
          this.mostrarToast('success', 'Registro exitoso', response.data.mensaje || 'Registro completado');
          await this.cargarAsistencia();
          await this.cargarEstadisticas();
        } else {
          this.mostrarToast('error', 'Error', response.data?.mensaje || response.error || 'Error al registrar');
        }
      } catch (error) {
        console.error('Error en registro manual:', error);
        this.mostrarToast('error', 'Error', error.message || 'Error al registrar');
      } finally {
        this.guardandoManual = false;
      }
    },
    
    // ========== DETALLE ==========
    verDetalle(registro) {
      this.detalleRegistro = registro;
      this.showDetalleModal = true;
    },
    
    closeDetalleModal() {
      this.showDetalleModal = false;
      this.detalleRegistro = null;
    },
    
    // ========== ORDENAMIENTO ==========
    ordenarRegistros(registros, criterio) {
      const sorted = [...registros];
      
      switch (criterio) {
        case 'fecha_desc':
          sorted.sort((a, b) => new Date(b.fechaHora) - new Date(a.fechaHora));
          break;
        case 'fecha_asc':
          sorted.sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora));
          break;
        case 'nombre':
          sorted.sort((a, b) => (a.nombreCompleto || '').localeCompare(b.nombreCompleto || ''));
          break;
        case 'nombre_desc':
          sorted.sort((a, b) => (b.nombreCompleto || '').localeCompare(a.nombreCompleto || ''));
          break;
        default:
          break;
      }
      
      return sorted;
    },
    
    // ========== UTILIDADES ==========
    getImageUrl(fotoPerfil) {
      if (!fotoPerfil) return null;
      return obtenerUrlImagen(fotoPerfil);
    },
    
    formatDate(date) {
      if (!date) return '';
      return dayjs(date).format('DD/MM/YYYY');
    },
    
    formatTime(date) {
      if (!date) return '';
      return dayjs(date).format('HH:mm:ss');
    },
    
    handleImageError(e) {
      e.target.src = this.defaultAvatar;
    },
    
    recargarDatos() {
      this.cargarAsistencia();
      this.cargarEstadisticas();
      this.cargarEstadoLector();
      this.mostrarToast('info', 'Actualizando', 'Datos recargados correctamente');
    },
    
    clearFilters() {
      this.searchQuery = '';
      this.filterTipo = 'all';
      this.filterMetodo = 'all';
      this.sortBy = 'fecha_desc';
      this.currentPage = 1;
    },
    
    // ========== TOAST ==========
    mostrarToast(type, title, message) {
      this.toast.type = type;
      this.toast.title = title;
      this.toast.message = message;
      this.toast.show = true;
      setTimeout(() => { this.toast.show = false; }, 4000);
    },
    
    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@media (max-width: 768px) {
  button {
    min-height: 44px;
  }
  
  input, select, textarea {
    font-size: 16px !important;
  }
}
</style>